<!-- @format -->

<template>
	<slot :model-value="checkIds" :on-show-dialog="onShowDialog" v-bind="$attrs">
		<el-select
			v-model="checkIds"
			multiple
			:teleported="false"
			class="selectUserClass"
			popper-class="selectUserPopper"
			placeholder="请选择数据"
			style="width: 100%"
			v-bind="$attrs"
			@click="onShowDialog"
			@remove-tag="(e) => onSelectDelete(e)"
		>
			<el-option v-for="item in checkValue" :key="item.value" :label="item.label" :value="item.value" />
		</el-select>
	</slot>
	<pc-dialog
		v-model="visible"
		append-to-body
		destroy-on-close
		:close-on-click-modal="false"
		v-bind="dialogProps"
		@on-cancel="onCancel"
		@on-confirm="onConfirm"
		@close="onCancel"
	>
		<el-select
			:model-value="cacheCheckIds"
			multiple
			:teleported="false"
			class="selectUserClass"
			popper-class="selectUserPopper"
			placeholder="请选择数据"
			style="width: 100%"
			@remove-tag="(e) => onDelete(e)"
		>
			<el-option v-for="item in cacheCheckValue" :key="item.value" :label="item.label" :value="item.value" />
		</el-select>

		<el-input v-model="filterText" class="filter-input" placeholder="请输入关键字回车查询" @keyup.enter="onFilter" />
		<div class="content">
			<el-tree
				ref="treeRef"
				:data="treeList"
				show-checkbox
				default-expand-all
				:default-checked-keys="checkIds"
				:filter-node-method="filterNode"
				check-on-click-node
				:check-strictly="isCheckParent"
				v-bind="treeProps"
				@check="handleCheckChange"
			/>
		</div>
	</pc-dialog>
</template>

<script setup lang="ts">
	import { ElTree } from 'element-plus';
	import { flatArr } from './utils';
	import { PcDialog } from '@/components';

	interface Props {
		modelValue: string[];
		treeList: any[];
		dialogProps?: Record<string, any>;
		treeProps?: Record<string, any>;
		isCheckParent?: boolean; // 是否可以选择父节点，和isLeaf配套使用
		filterNodeMethod?: Function;
		multiple?: boolean; // 是否单选
	}

	interface CheckValue {
		label: string;
		value: string;
	}

	const props = withDefaults(defineProps<Props>(), {
		treeProps: () => ({}),
		isCheckParent: true,
		multiple: true,
	});

	const emit = defineEmits(['update:modelValue', 'onConfirm']);

	const attrs = useAttrs();

	const visible = ref<boolean>(false);

	const treeRef = ref<InstanceType<typeof ElTree>>();

	// 选中的ids
	const checkIds = computed({
		get() {
			return props.modelValue || [];
		},
		set(val) {
			emit('update:modelValue', val);
		},
	});

	// 选中的带有label、value的数组
	const checkValue = ref<CheckValue[]>([]);

	// 缓存checkValue，退出时重置数据
	const cacheCheckValue = ref<CheckValue[]>([]);

	// 缓存的checkIds
	const cacheCheckIds = computed(() => cacheCheckValue.value.map((i) => i.value));

	const filterText = ref<string>('');

	const filterNode = (value, data) => {
		if (!value) return true;
		return props.filterNodeMethod ? props.filterNodeMethod(value, data) : true;
	};

	const onFilter = () => {
		treeRef.value!.filter(filterText.value);
	};

	const onShowDialog = () => {
		if (!attrs.disabled) {
			visible.value = true;
			cacheCheckValue.value = toRaw(checkValue.value);
		}
	};

	const getIsLeafList = (arr: any[], isLeaf: string | Function) =>
		arr.filter((i) => (props.isCheckParent ? true : typeof isLeaf === 'function' ? isLeaf(i) : i[isLeaf])) || [];

	const handleCheckChange = (e = {}) => {
		const labelKey = props.treeProps.props.label;
		const nodeKey = props.treeProps['node-key'];
		const isLeaf = props.treeProps.props.isLeaf;

		if (!props.isCheckParent && !isLeaf) {
			console.error('只能选择叶子节点时treeProps必须传入isLeaf属性,否则认为所有节点为父节点');
		}

		// 单选模式
		if (!props.multiple) {
			const checkVal = getIsLeafList(flatArr([e]), isLeaf).slice(0, 1)[0];
			const currentCheckKey = checkVal && checkVal[nodeKey];
			const isChecked = cacheCheckValue.value.some((it) => it.value === currentCheckKey);

			treeRef.value?.setCheckedKeys(isChecked ? [] : [currentCheckKey], false);

			cacheCheckValue.value =
				isChecked || !checkVal
					? []
					: [checkVal].map((i) => ({
							label: i[labelKey],
							value: i[nodeKey],
					  }));
		} else {
			const checkVal = getIsLeafList(treeRef.value?.getCheckedNodes() || [], isLeaf);

			cacheCheckValue.value = checkVal.map((i) => ({
				label: i[labelKey],
				value: i[nodeKey],
			}));
		}
	};

	const onSelectDelete = (value) => {
		checkIds.value = checkIds.value.filter((i) => i !== value);
		checkValue.value = checkValue.value.filter((i) => i.value !== value);
	};

	const onDelete = (value) => {
		treeRef.value?.setChecked(value, false, true);
		handleCheckChange();
	};

	const onCancel = (callback) => {
		filterText.value = '';
		visible.value = false;

		callback && callback();
	};

	const onConfirm = (callback) => {
		filterText.value = '';
		visible.value = false;
		checkValue.value = cacheCheckValue.value;
		checkIds.value = cacheCheckValue.value.map((i) => i.value);
		emit('onConfirm', checkIds.value, checkValue.value);
		callback();
	};

	onMounted(() => {
		const flatTreeList = flatArr(props.treeList);

		const labelKey = props.treeProps.props.label;
		const nodeKey = props.treeProps['node-key'];

		checkValue.value = flatTreeList
			.filter((itm) => checkIds.value.includes(itm[nodeKey]))
			.map((i) => ({
				label: i[labelKey],
				value: i[nodeKey],
			}));
	});

	defineExpose({ treeRef });
</script>

<style lang="scss" scoped>
	.content {
		border: 1px solid var(--el-border-color);
		padding: 15px;
		border-radius: 5px;
		max-height: 400px;
		overflow-y: scroll;
	}

	.filter-input {
		margin: 5px 0;
	}

	.selectUserClass {
		:deep(.selectUserPopper) {
			visibility: hidden;
		}
	}
</style>
